import React, { useEffect } from 'react';

import { assertExhaustiveSwitch } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { LayoutCoordinateVisuals } from '../../library/layout-geometry/LayoutCoordinateVisuals';
import { classifyClickDuringWorkLineInsertion } from '../../library/layout-geometry/work-lines';
import {
  getSnapVisualTypeForSnappedMousePosition,
  SnapMousePositionResult,
} from '../../library/layout-snapping/point-snapping/snapMousePosition';
import {
  handleSceneClickDuringWorkLineInsertion,
  updateWorkLineGestureLineType,
} from '../../store/actions/layout/workLineActions';
import { isPickingActive } from '../../store/selectors/pickingSelectors';
import { getWorkLineInsertionInfo } from '../../store/selectors/workLineInsertionSelectors';
import { SnapVisual } from '../dimension/SnapVisual';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../util/events/types';
import { HoveredEntitySnapVisual } from './HoveredEntitySnapVisual';
import { layoutColors } from './layoutColors';
import { useWorkLineInsertionSnapper } from './useWorkLineInsertionSnapper';

/**
 * Responsible for the event handlers that actually drive in-scene work line insertion, as well as rendering snap
 *   visuals during that process.
 *
 * Note that the actual work line preview visuals are rendered separately in `WorkLineInsertionPreviewVisuals`.
 */
export function WorkLineInsertionPickingController() {
  const dispatch = useAppDispatch();
  const insertionInfo = useAppSelector(getWorkLineInsertionInfo);
  const pickingActive = useAppSelector(isPickingActive);
  const isPickedPointPending = useAppSelector((state) => state.application.pendingPickedPointMovement != null);

  const { liveSnap, liveLine, snapAtWorldPosition } = useWorkLineInsertionSnapper();

  const isInserting = insertionInfo != null;

  useEffect(() => {
    // don't interfere with manual explicit picking
    if (!isInserting || pickingActive) return;

    function onSceneClick(event: GlobalThreeJSDomObjectEvent) {
      const snap = snapAtWorldPosition(event.eventPosition.worldPosition);

      dispatch(
        handleSceneClickDuringWorkLineInsertion(snap, {
          clientPosition: event.eventPosition.clientPosition,
          modifiers: event.metadata,
        }),
      );

      return false; // stop propagation
    }

    threeJsDom.addGlobalListener('click', onSceneClick);
    return () => void threeJsDom.removeGlobalListener('click', onSceneClick);
  }, [dispatch, isInserting, pickingActive, snapAtWorldPosition]);

  const gestureLineType = insertionInfo?.draft.phase === 'gesturing' ? (liveLine?.type ?? null) : null;
  useEffect(() => {
    dispatch(updateWorkLineGestureLineType(gestureLineType));
  }, [dispatch, gestureLineType]);

  if (insertionInfo == null || insertionInfo.draft.phase === 'reviewing' || liveSnap == null) return null;

  // the pending point's snap visual is rendered by `PointPickingVisualsAndEventListeners` instead of the live one
  if (isPickedPointPending) return null;

  const { pointSnap, lineSnap } = liveSnap;

  switch (insertionInfo.draft.phase) {
    // if no selection has been made yet we should show a snap visual for the hovered target
    case 'idle': {
      const hoverType = classifyClickDuringWorkLineInsertion(liveSnap).type;

      switch (hoverType) {
        case 'point':
          return <PointSnapCursor pointSnap={pointSnap} />;

        case 'line': {
          if (lineSnap == null) return null;

          return (
            <LayoutCoordinateVisuals>
              <HoveredEntitySnapVisual snap={lineSnap} color={layoutColors.entitySnap} />
            </LayoutCoordinateVisuals>
          );
        }

        default:
          assertExhaustiveSwitch(hoverType);
      }
    }

    // if the first selection has been made, we only show a snap visual appropriate for the expected second selection
    case 'gesturing': {
      // during perpendicular and point-to-point gesturing, a point is being hovered
      if (
        liveLine?.type === 'perpendicular' ||
        (insertionInfo.draft.firstSelection.type === 'point' && liveLine?.type === 'point-to-point')
      ) {
        return <PointSnapCursor pointSnap={pointSnap} />;
      }

      // a line is being hovered during cases that accept a line (division and exact)
      if ((liveLine?.type === 'division' || liveLine?.type === 'exact') && lineSnap != null) {
        return (
          <LayoutCoordinateVisuals>
            <HoveredEntitySnapVisual snap={lineSnap} color={layoutColors.entitySnap} />
          </LayoutCoordinateVisuals>
        );
      }

      // in all other cases, we don't want a snap visual
      break;
    }

    default:
      assertExhaustiveSwitch(insertionInfo.draft);
  }

  return null;
}

/** The point snap cursor, placed at the snapped layout point (wrapped so layout coordinates map to design space). */
function PointSnapCursor({ pointSnap }: { pointSnap: SnapMousePositionResult }) {
  const snapVisualType = getSnapVisualTypeForSnappedMousePosition(pointSnap);
  if (snapVisualType == null) return null;

  return (
    <LayoutCoordinateVisuals>
      <SnapVisual snapType={snapVisualType} position={pointSnap.point.to3D().toArray()} />
    </LayoutCoordinateVisuals>
  );
}
